// All money is USDT on TON in its smallest unit (6 decimals), held as bigint.
// Floats never touch a balance. Fees, stake limits and referral terms come from the live rules (rules.ts).
import { rules, STAKE_CEILING } from "./rules.js";

export type Units = bigint;

export const USDT_DECIMALS = 6;
export const UNITS_PER_USDT = 1_000_000n;

export const FEE_BPS = 400n; // the default: 4% of the total pot, taken from the winner's payout
export const REFERRAL_SHARE_PCT = 20n; // inviter's share of the fee on their friend's wins
export const REFERRAL_DAYS = 90;
export const REFERRAL_MIN_WINS = 5; // the friend must win 5 battles before the inviter earns
export const FEE_FREE_WINS = 5; // a new friend's first 5 wins carry no fee
export const WITHDRAW_FEE: Units = 100_000n; // 0.10 USDT
export const MIN_DEPOSIT: Units = 2n * UNITS_PER_USDT; // one minimum battle
export const MAX_BALANCE: Units = 10_000n * UNITS_PER_USDT; // anything deposited above this goes back to the wallet
export const MAX_PAYOUT: Units = 10_000n * UNITS_PER_USDT; // the vault's limit per transfer: larger withdrawals are split
export const MIN_WITHDRAW: Units = 5n * UNITS_PER_USDT; // taken from the balance; the wallet receives it less the 0.10 fee
export const MIN_STAKE: Units = 2n * UNITS_PER_USDT;
export const MAX_STAKE: Units = 1000n * UNITS_PER_USDT;
export const MATCH_TOLERANCE_PCT = 20n; // custom stakes match anyone within 20%

export function usdt(amount: string | number): Units {
  const s = typeof amount === "number" ? amount.toFixed(USDT_DECIMALS) : amount.trim();
  const m = /^(\d+)(?:\.(\d{1,6}))?$/.exec(s);
  if (!m) throw new Error(`Not a valid USDT amount: ${amount}`);
  const whole = BigInt(m[1]!);
  const frac = BigInt((m[2] ?? "").padEnd(USDT_DECIMALS, "0"));
  return whole * UNITS_PER_USDT + frac;
}

export function formatUsdt(units: Units, decimals = 2): string {
  const neg = units < 0n;
  const abs = neg ? -units : units;
  const whole = abs / UNITS_PER_USDT;
  const frac = (abs % UNITS_PER_USDT).toString().padStart(USDT_DECIMALS, "0").slice(0, decimals);
  return `${neg ? "-" : ""}${whole}${decimals > 0 ? "." + frac : ""}`;
}

export interface Payout {
  pot: Units;
  fee: Units;
  winnerPayout: Units;
  referralShare: Units;
  houseShare: Units;
}

/** Splits a settled pot. The fee is rounded down, so rounding always favours the player. */
/** The fee during a power hour: half the usual. */
export const POWER_FEE_BPS = 200n;

/**
 * `feeBps` is the battle's own fee, fixed when it started (a power hour's, say); without it, today's battle fee.
 * The inviter's share is the live rules' share of that fee.
 */
export function splitPot(stake: Units, opts: { feeFree?: boolean; hasReferrer?: boolean; feeBps?: bigint } = {}): Payout {
  if (stake <= 0n) throw new Error("Stake must be positive");
  const pot = stake * 2n;
  const fee = opts.feeFree ? 0n : (pot * (opts.feeBps ?? BigInt(rules().feeBps))) / 10_000n;
  const referralShare = opts.hasReferrer ? (fee * BigInt(rules().referral.sharePct)) / 100n : 0n;
  return { pot, fee, winnerPayout: pot - fee, referralShare, houseShare: fee - referralShare };
}

/** Two custom stakes match when the larger is within 20% of the smaller. Both play the lower stake. */
export function matchStakes(a: Units, b: Units): Units | null {
  const lo = a < b ? a : b;
  const hi = a < b ? b : a;
  return hi * 100n <= lo * (100n + MATCH_TOLERANCE_PCT) ? lo : null;
}

/** Stake ceiling for a player, from the live tiers: 50 to start, 250 after 20 battles, 1000 after 100 battles at level 10. */
export function stakeLimit(battles: number, level: number): Units {
  let max = rules().tiers[0]!.maxStake;
  for (const t of rules().tiers) if (battles >= t.battles && level >= t.level) max = Math.max(max, t.maxStake);
  return BigInt(max) * UNITS_PER_USDT;
}

/**
 * Whether an inviter earns from their friend's next win. The inviter earns 20% of the fee only on
 * battles the friend wins, starting once the friend has passed screening and won 5 battles,
 * for 90 days from the day the friend joined. The first 5 wins are fee free, so earnings start at win 6.
 */
export function referralActive(p: { screened: boolean; friendWins: number; joinedAt: Date; now: Date }): boolean {
  if (!p.screened || p.friendWins < rules().referral.freeWins) return false;
  return p.now.getTime() - p.joinedAt.getTime() < REFERRAL_DAYS * 86_400_000;
}

/** A friend's win is fee free while they have fewer than 5 wins. */
export function isFeeFreeWin(priorWins: number, invited: boolean): boolean {
  return invited && priorWins < rules().referral.freeWins;
}

/**
 * The deposit screen will not start a deposit under 2 USDT. If someone sends less straight to the
 * vault anyway, it is still credited: we never keep a player's money.
 */
export function validateDeposit(amount: Units): string | null {
  return amount < MIN_DEPOSIT ? "Minimum deposit is 2 USDT" : null;
}

/**
 * Withdrawals start at 5 USDT so gas is never spent on dust. The 0.10 fee comes out of the amount,
 * so withdrawing 5 delivers 4.90. Closing an account withdraws everything left, even under 5,
 * so no balance is ever stuck.
 */
export function validateWithdrawal(amount: Units, available: Units, opts: { closingAccount?: boolean } = {}): string | null {
  if (amount > available) return "Not enough balance";
  if (opts.closingAccount) {
    if (amount !== available) return "Closing withdraws your whole balance";
    return amount > WITHDRAW_FEE ? null : "Balance is too small to cover the 0.10 USDT network fee";
  }
  if (amount < MIN_WITHDRAW) return "Minimum withdrawal is 5 USDT";
  return null;
}

export function withdrawalReceived(amount: Units): Units {
  return amount - WITHDRAW_FEE;
}

export function validateStake(stake: Units, battles: number, level: number): string | null {
  const min = BigInt(rules().minStake) * UNITS_PER_USDT;
  if (stake < min) return `Minimum stake is ${rules().minStake} USDT`;
  if (stake > BigInt(STAKE_CEILING) * UNITS_PER_USDT) return `Maximum stake is ${STAKE_CEILING} USDT`;
  if (stake > stakeLimit(battles, level)) return `Your limit is ${formatUsdt(stakeLimit(battles, level), 0)} USDT`;
  return null;
}
